import { AppButton, AppIconButton, AppMemberDisplay, AppTextarea } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { GetInquiryCoordinationsResponse } from 'backend/types'
import { Pencil, Trash2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { FIELD_MAX_LENGTHS } from 'shared'
import { useCoordinationItemEdit } from '../hooks/useCoordinationItemEdit'

type CoordinationItemProps = {
  coordination: GetInquiryCoordinationsResponse['inquiry_coordinations'][number]
  onEdit: (id: number, content: string) => Promise<void>
  onDelete: (id: number) => void
  onEditingChange?: (isEditing: boolean) => void
  isInquiryCompleted?: boolean
}

export function CoordinationItem({
  coordination,
  onEdit,
  onDelete,
  onEditingChange,
  isInquiryCompleted,
}: CoordinationItemProps) {
  const isEdited = coordination.created_at !== coordination.updated_at
  const createdAt = DateTime.fromSeconds(coordination.created_at).toFormat('yyyy/MM/dd HH:mm')

  const {
    isEditing,
    editContent,
    editContentError,
    handleEditStart,
    handleEditSave,
    handleEditCancel,
    handleContentChange,
  } = useCoordinationItemEdit({
    initialContent: coordination.content,
    onEdit: (content) => onEdit(coordination.id, content),
    onEditingChange,
  })

  return (
    <div className='rounded-md border p-3'>
      <div className='mb-1 flex items-center justify-between'>
        <div className='flex items-center gap-2 text-sm'>
          <AppMemberDisplay
            employeeNumber={coordination.employee_number}
            name={coordination.member_name}
            emptyDisplay='dash'
          />
          <span className='text-muted-foreground'>{createdAt}</span>
          {isEdited && <span className='text-xs text-muted-foreground'>(編集済み)</span>}
        </div>
        <div className='flex gap-1'>
          {!isEditing && (
            <>
              <AppIconButton description='編集' size='sm' onClick={handleEditStart} disabled={isInquiryCompleted}>
                <Pencil className='size-3.5' />
              </AppIconButton>
              <AppIconButton
                description='削除'
                size='sm'
                onClick={() => onDelete(coordination.id)}
                disabled={isInquiryCompleted}
              >
                <Trash2 className='size-3.5' />
              </AppIconButton>
            </>
          )}
        </div>
      </div>
      {isEditing ? (
        <div className='space-y-2'>
          <AppTextarea
            value={editContent}
            onChange={(e) => handleContentChange(e.target.value)}
            rows={3}
            autoFocus
            maxLength={FIELD_MAX_LENGTHS.INQUIRY_COORDINATION_CONTENT}
            showCharCount
            error={editContentError}
          />
          <div className='flex justify-end gap-2'>
            <AppButton size='sm' variant='outline' onClick={handleEditCancel}>
              キャンセル
            </AppButton>
            <AppButton size='sm' variant='save' onClick={handleEditSave} disabled={editContent.trim() === ''}>
              保存
            </AppButton>
          </div>
        </div>
      ) : (
        <p className='whitespace-pre-wrap break-all text-sm'>{coordination.content}</p>
      )}
    </div>
  )
}
